/**
 * PasswordPolicyNudge — soft, non-blocking Settings banner that appears only
 * when the signed-in user's current password no longer meets our updated
 * password standard (backend `password_meets_policy` flag set to false on
 * the last successful login).
 *
 * Founder decision (Feb 2026, session 237, option 2c): existing beta users
 * are NOT forced to change their password. We simply invite them to update
 * it. Rendering is skipped entirely when the flag is undefined (Google-only
 * accounts, or a user who hasn't logged in since the policy shipped).
 *
 * Session 262 (Feb 2026): the nudge is now clickable — tapping it opens the
 * Change Password sub-tab inside Account Settings and scrolls it into view.
 * Previously the copy said "open the Change Password section below" but no
 * interaction was wired up, so users read it as a static reminder.
 */
import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { KeyRound, ArrowRight } from 'lucide-react';

const PasswordPolicyNudge = ({ user }) => {
  if (!user || user.password_meets_policy !== false) return null;

  const handleOpenChangePassword = () => {
    // UserProfileSettings listens for this event and (a) switches its
    // internal sub-tab to 'password' and (b) scrolls the Change Password
    // card into view. See useEffect in UserProfileSettings.js.
    window.dispatchEvent(new CustomEvent('mif:open-account-section', { detail: 'password' }));
  };

  return (
    <button
      type="button"
      onClick={handleOpenChangePassword}
      data-testid="password-policy-nudge"
      className="w-full text-left group"
      aria-label="Open Change Password section"
    >
      <Card className="border-amber-200 bg-amber-50/70 group-hover:bg-amber-50 transition">
        <CardContent className="py-4 flex items-start gap-3">
          <div className="w-9 h-9 rounded-full bg-amber-100 border border-amber-200 flex items-center justify-center shrink-0">
            <KeyRound className="w-4 h-4 text-amber-700" />
          </div>
          <div className="flex-1">
            <p className="text-sm font-semibold text-amber-900">
              Please strengthen your password
            </p>
            <p className="text-xs text-amber-800 mt-1 leading-relaxed">
              Your current password no longer meets our updated standard. Tap here to open the <em>Change Password</em> section and set a new one with at least 8 characters, an uppercase letter, a lowercase letter, a number and a special character (like !@#$%).
            </p>
          </div>
          <ArrowRight className="w-4 h-4 text-amber-700 shrink-0 mt-1 group-hover:translate-x-0.5 transition-transform" />
        </CardContent>
      </Card>
    </button>
  );
};

export default PasswordPolicyNudge;
